import { Formik } from 'formik';
import { useState, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBackIosNew } from 'react-icons/md';
import { countrySchema } from '../../../../../constants';
import { DAV_APIS } from '../../../../../adapters';
import { Alert, SEO } from '../../../../commons';
import CountryForm from '../CountryForm';
import { useCountry, useSearchQuery } from '../../../../../hooks';
import DashboardLoader from '../../../sections/Loader/DashboardLoader';
import { Button } from '../../../../ui/Button';

const EditCountryPage: FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const countryId = useSearchQuery('country') as string;

  const { country, loading: isFetching } = useCountry(countryId);

  const newCountrySubmitHandler = async (values: any) => {
    setLoading(true);
    try {
      const data = {
        name: values.name,
        title: values.title,
        description: values.description,
        countrySummary: values.countrySummary,
        countryImage: values.imageFile,
      };
      const res = await DAV_APIS.country.editCountry(data, countryId);
      setLoading(false);
      if (res.status === 200) {
        Alert({ variant: 'success', message: 'Country updated successfully' });
        navigate('/dashboard/countries');
      }
    } catch (error) {
      setLoading(false);
      Alert({ variant: 'error', message: 'Failed to update country, Please try again' });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className='w-full space-y-6 pt-4 pb-10'>
      <div className='flex items-center'>
        <button
          onClick={() => navigate('/dashboard/countries')}
          type='button'
          className='flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors font-semibold'
        >
          <MdArrowBackIosNew className='w-4 h-4' />
          <span>Back to Destinations</span>
        </button>
      </div>

      <div className='bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm min-h-[400px]'>
        <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white mb-8 text-center tracking-tight'>
          Edit Destination
        </h1>

        {isFetching ? (
          <div className='py-20 flex justify-center'>
            <DashboardLoader />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              name: country.name || '',
              title: country.title || '',
              description: country.description || '',
              imageCover: country.countryImage || '',
              imageFile: '',
              countrySummary: country.summary || '',
            }}
            validationSchema={countrySchema}
            onSubmit={async (values, { resetForm }) => {
              await newCountrySubmitHandler(values);
              resetForm();
            }}
          >
            {({ handleSubmit, values, setFieldValue, dirty, isValid }) => (
              <form onSubmit={handleSubmit} className='w-full'>
                <CountryForm values={values} setFieldValue={setFieldValue} />
                <div className='flex justify-center mt-10 border-t border-gray-100 dark:border-gray-700 pt-8'>
                  <Button
                    type='submit'
                    variant='primary'
                    loading={loading}
                    disabled={!dirty || !isValid}
                    className='w-full sm:w-64'
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        )}
      </div>
      <SEO
        isEdit
        pageType='country'
        initialData={{
          title: country.name,
          description: country.description,
          image: country.countryImage,
          slug: country.slug,
        }}
      />
    </div>
  );
};

export default EditCountryPage;
